<template>
  <main class="home">
    <Header
      navbarType="goBack"
      :screenTitle="t('CL_WITHDRAWAL.WITHDRAWAL_INFORMATION')"
      @clicked="goBack()"
    />
    <div class="px-3">
      <div v-if="pendingWithdrawal.type === 'CASH_TRANSFER'">
        <InfoCard
          :title="`<i class='fas fa-hourglass-end'></i> ${t(
            'CL_WITHDRAWAL.REQUEST_IN_PROGRESS_TITLE'
          )}`"
        >
          <div>
            {{
              t(
                "CL_WITHDRAWAL.REQUEST_IS_PROGRESS_TRANSFER_PENDING_DESCRIPTION"
              )
            }}
          </div>
        </InfoCard>
        <div class="info-container mt-4 px-1">
          <div class="info-line">
            <span>{{ t("CL_WITHDRAWAL.WITHDRAWAL_AMOUNT") }}</span>
            <span>{{ currencyFormat(pendingWithdrawal.amount) }}</span>
          </div>
        </div>
      </div>
      <div v-if="pendingWithdrawal.type === 'WITHDRAWAL'">
        <InfoCard
          v-if="pendingWithdrawal.status !== 'READY'"
          :title="`<i class='fas fa-hourglass-end'></i> ${t(
            'CL_WITHDRAWAL.REQUEST_IN_PROGRESS_TITLE'
          )}`"
        >
          <div>
            {{
              t("CL_WITHDRAWAL.REQUEST_IN_PROGRESS_PICKUP_PENDING_DESCRIPTION")
            }}
          </div>
        </InfoCard>
        <InfoCard v-else :title="celebrationTitle">
          <div>{{ t("CL_WITHDRAWAL.REQUEST_READY_PICKUP_DESCRIPTION") }}</div>
        </InfoCard>
        <div class="info-container mt-4 px-1">
          <div class="info-line">
            <span>{{ t("CL_WITHDRAWAL.WITHDRAWAL_AMOUNT") }}</span>
            <span>{{ currencyFormat(pendingWithdrawal.amount) }}</span>
          </div>
          <div class="info-line" v-if="showSummaryField('reference')">
            <span>{{ t("CL_WITHDRAWAL.REFERENCE") }}</span>
            <span>
              {{
                pendingWithdrawal?.pickupInformation?.reference ??
                t("CL_WITHDRAWAL.AWAITING_INFO")
              }}
            </span>
          </div>
          <div class="info-line" v-if="showSummaryField('contract')">
            <span>{{ t("CL_WITHDRAWAL.AGREEMENT") }}</span>
            <span>
              {{
                pendingWithdrawal?.pickupInformation?.contract ??
                t("CL_WITHDRAWAL.AWAITING_INFO")
              }}
            </span>
          </div>
          <div class="info-line" v-if="showSummaryField('concept')">
            <span>{{ t("CL_WITHDRAWAL.CONCEPT") }}</span>
            <span>
              {{
                pendingWithdrawal?.pickupInformation?.concept ??
                t("CL_WITHDRAWAL.AWAITING_INFO")
              }}
            </span>
          </div>
          <div class="info-line" v-if="showSummaryField('verificationCode')">
            <span>{{ t("CL_WITHDRAWAL.VERIFICATION_CODE") }}</span>
            <span>
              {{
                pendingWithdrawal?.verificationCode ??
                t("CL_WITHDRAWAL.AWAITING_INFO")
              }}
            </span>
          </div>
          <div class="info-line">
            <span>{{ t("CL_WITHDRAWAL.AVAILABLE_UNTIL") }}</span>
            <span>
              {{
                pendingWithdrawal?.pickupInformation?.expirationTime
                  ? new Date(
                      pendingWithdrawal.pickupInformation.expirationTime * 1000
                    ).toLocaleDateString()
                  : t("CL_WITHDRAWAL.AWAITING_INFO")
              }}
            </span>
          </div>
        </div>
        <div
          v-if="pendingWithdrawal.status === 'READY'"
          class="px-1 mt-5 text-black"
        >
          <div class="fw-bold">{{ t("INSTRUCTIONS") }}</div>
          <ol class="instructions-list">
            <li>
              {{ t("CL_WITHDRAWAL.PICKUP_INSTRUNCTION_1") }}
            </li>
            <li>
              {{ t("CL_WITHDRAWAL.PICKUP_INSTRUNCTION_2") }}
            </li>
            <li>
              {{ t("CL_WITHDRAWAL.PICKUP_INSTRUNCTION_3") }}
            </li>
          </ol>
        </div>
      </div>
    </div>
  </main>
</template>
<script lang="ts" setup>
import { computed } from "vue";
import Header from "@/shared-components/consumer/Header.vue";
import InfoCard from "@/apps/credit-line/components/InfoCard.vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { IWithdrawal } from "@/apps/credit-line/models/CreditLineDetails";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import currencyFormat from "@/apps/credit-line/helpers/currencyFormatter.helper";
import { safeAndroidCall } from "@/apps/credit-line/helpers/safe-android-call.helper";

const props = defineProps<{
  pendingWithdrawal: IWithdrawal;
  instructionFieldsSetting: string[];
}>();

const { t } = useI18n();
const celebrationTitle = computed(
  () =>
    `<img src='${useAsset(
      "img/credit-line/celebration.svg"
    )}'  alt="celebration"/> ${t("CL_WITHDRAWAL.REQUEST_READY_PICKUP_TITLE")}`
);

const showSummaryField = (id: string): boolean => {
  return (
    props.instructionFieldsSetting &&
    props.instructionFieldsSetting.some((x) => x === id)
  );
};

const goBack = () => {
  if (isAndroid()) {
    safeAndroidCall("dismissWebView");
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
.info-container {
  display: flex;
  flex-direction: column;
  :first-child {
    border-top: 0;
  }
}
.info-line {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  padding: 10px 0;
  border-top: 2px solid #f2f2f2;
  color: black;
}
.instructions-list {
  li {
    margin-top: 15px;
  }
}
</style>
